import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AlertTriangle } from 'lucide-react'

interface BulkDeleteDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  selectedProducts: string[]
  onBulkDelete: (_productIds: string[]) => Promise<void>
  isLoading?: boolean
}

export function BulkDeleteDialog({
  open,
  onOpenChange,
  selectedProducts,
  onBulkDelete,
  isLoading = false,
}: BulkDeleteDialogProps) {
  const handleConfirm = async () => {
    await onBulkDelete(selectedProducts)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Confirm Bulk Delete
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete {selectedProducts.length} selected product
            {selectedProducts.length > 1 ? 's' : ''}? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-800">
              <strong>Warning:</strong> Deleting products will permanently remove them from the
              database. This action cannot be reversed.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleConfirm} disabled={isLoading}>
            {isLoading
              ? 'Deleting...'
              : `Delete ${selectedProducts.length} product${selectedProducts.length > 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
